/**
 * AvatarUpload — shared file/URL image input for the admin CMS.
 *
 * Features:
 *  - Click OR drag-drop file picker
 *  - Live circular / square / rectangular thumbnail preview
 *  - Optional client-side crop-to-square modal (canvas, no external deps)
 *  - Native file picker with format + size validation before upload
 *  - Uploads to Emergent Object Storage via POST /api/media/upload
 *  - Falls back to "paste URL" for external hosts
 *
 * Props:
 *  - value: current image URL string
 *  - onChange(url: string): called with the new URL after upload / paste / remove
 *  - folder?: string — Object Storage folder (default: "general")
 *  - shape?: 'circle' | 'square' | 'rect' (default 'circle')
 *          circle & square open the crop modal for raster formats.
 *          rect skips cropping entirely (good for hero backgrounds, banners).
 *  - hint?: string — custom criteria hint text
 *  - testId?: string — data-testid prefix (default 'avatar-upload')
 *  - accept?: string — file input accept attribute
 *  - previewSize?: number — px size for the round/square preview (default 64)
 */

import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Upload, Loader2, X, ImagePlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IMG_MAX_MB = 100;
const IMG_ALLOWED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const SHOULD_CROP = (mime) => mime === 'image/jpeg' || mime === 'image/png' || mime === 'image/webp';
const DEFAULT_HINT = `JPG / PNG / WEBP / GIF / SVG · up to ${IMG_MAX_MB} MB`;

// Resolve an image URL for display (handles cloud, legacy, external).
export const resolveImgSrc = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  return `${API_URL}${u.startsWith('/') ? '' : '/'}${u}`;
};

// ─────────────────────────────────────────────────────────────
// Canvas-based crop modal (extracted from AdminSpeakers)
// ─────────────────────────────────────────────────────────────
const CROP_OUT_SIZE = 512;
const CROP_VIEW_SIZE = 340;

const ImageCropModal = ({ file, shape = 'circle', onCancel, onConfirm }) => {
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const isCircle = shape === 'circle';

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => {
      const min = Math.max(CROP_VIEW_SIZE / i.width, CROP_VIEW_SIZE / i.height);
      setImg(i);
      setMinScale(min);
      setScale(min);
      setOffset({
        x: (CROP_VIEW_SIZE - i.width * min) / 2,
        y: (CROP_VIEW_SIZE - i.height * min) / 2,
      });
    };
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!img || !canvasRef.current) return;
    const cv = canvasRef.current;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, CROP_VIEW_SIZE, CROP_VIEW_SIZE);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, CROP_VIEW_SIZE, CROP_VIEW_SIZE);
    ctx.drawImage(img, offset.x, offset.y, img.width * scale, img.height * scale);
  }, [img, scale, offset]);

  const clampOffset = (o, s = scale) => {
    if (!img) return o;
    const w = img.width * s;
    const h = img.height * s;
    return {
      x: Math.min(0, Math.max(CROP_VIEW_SIZE - w, o.x)),
      y: Math.min(0, Math.max(CROP_VIEW_SIZE - h, o.y)),
    };
  };

  const onMouseDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: offset.x, origY: offset.y };
    e.currentTarget.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clampOffset({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy }));
  };
  const onMouseUp = (e) => { dragRef.current = null; e.currentTarget.style.cursor = 'grab'; };
  const onTouchStart = (e) => {
    const t = e.touches[0];
    dragRef.current = { startX: t.clientX, startY: t.clientY, origX: offset.x, origY: offset.y };
  };
  const onTouchMove = (e) => {
    if (!dragRef.current) return;
    const t = e.touches[0];
    const dx = t.clientX - dragRef.current.startX;
    const dy = t.clientY - dragRef.current.startY;
    setOffset(clampOffset({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy }));
  };
  const onTouchEnd = () => { dragRef.current = null; };

  const changeScale = (next, e) => {
    if (!img) return;
    const clamped = Math.min(minScale * 4, Math.max(minScale, next));
    const cx = e?.nativeEvent?.offsetX ?? CROP_VIEW_SIZE / 2;
    const cy = e?.nativeEvent?.offsetY ?? CROP_VIEW_SIZE / 2;
    const ratio = clamped / scale;
    setScale(clamped);
    setOffset(clampOffset({
      x: cx - (cx - offset.x) * ratio,
      y: cy - (cy - offset.y) * ratio,
    }, clamped));
  };
  const onWheel = (e) => { e.preventDefault(); changeScale(scale - e.deltaY * 0.001, e); };

  const confirm = () => {
    if (!img || busy) return;
    setBusy(true);
    const out = document.createElement('canvas');
    out.width = CROP_OUT_SIZE;
    out.height = CROP_OUT_SIZE;
    const octx = out.getContext('2d');
    const factor = CROP_OUT_SIZE / CROP_VIEW_SIZE;
    octx.fillStyle = '#ffffff';
    octx.fillRect(0, 0, CROP_OUT_SIZE, CROP_OUT_SIZE);
    octx.drawImage(img,
      offset.x * factor, offset.y * factor,
      img.width * scale * factor, img.height * scale * factor);
    const outMime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    out.toBlob((blob) => {
      if (!blob) { setBusy(false); toast.error('Crop failed'); return; }
      const ext = outMime === 'image/png' ? '.png' : '.jpg';
      const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
      onConfirm(new File([blob], `${baseName}-cropped${ext}`, { type: outMime }));
    }, outMime, 0.92);
  };

  const maskClip = isCircle
    ? `circle(${CROP_VIEW_SIZE / 2}px at 50% 50%)`
    : `inset(0 0 0 0)`;
  const guideClass = isCircle ? 'rounded-full' : 'rounded';

  return (
    <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4" data-testid="crop-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Crop to square</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 -mt-2">
          Drag to reposition • scroll or use slider to zoom. The {isCircle ? 'circle' : 'square'} shows how the image will appear.
        </p>
        <div
          className="relative mx-auto rounded overflow-hidden bg-gray-900"
          style={{ width: CROP_VIEW_SIZE, height: CROP_VIEW_SIZE, cursor: 'grab', touchAction: 'none' }}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
          onWheel={onWheel} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
          data-testid="crop-viewport"
        >
          <canvas ref={canvasRef} width={CROP_VIEW_SIZE} height={CROP_VIEW_SIZE} className="block select-none pointer-events-none" />
          {isCircle && (
            <div className="absolute inset-0 pointer-events-none" style={{
              boxShadow: `0 0 0 9999px rgba(0,0,0,0.55)`,
              clipPath: maskClip,
              WebkitClipPath: maskClip,
            }} />
          )}
          <div className={`absolute inset-0 pointer-events-none border-2 border-white/70 ${guideClass}`} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 uppercase font-medium">Zoom</span>
          <input type="range" min={minScale} max={minScale * 4} step={0.01} value={scale}
            onChange={(e) => changeScale(parseFloat(e.target.value))}
            className="flex-1 accent-[#00457f]" data-testid="crop-zoom-slider" />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="crop-cancel-btn">Cancel</Button>
          <Button onClick={confirm} disabled={busy || !img} className="bg-[#00457f] hover:bg-[#003461] text-white" data-testid="crop-confirm-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</> : 'Use this crop'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// AvatarUpload — the exported component
// ─────────────────────────────────────────────────────────────
export const AvatarUpload = ({
  value,
  onChange,
  folder = 'general',
  shape = 'circle',       // 'circle' | 'square' | 'rect'
  hint,
  testId = 'avatar-upload',
  accept = 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml',
  previewSize = 64,
}) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const cropEnabled = shape === 'circle' || shape === 'square';
  const previewClass =
    shape === 'circle' ? 'rounded-full' :
    shape === 'square' ? 'rounded-md' :
    'rounded-md';
  // For rect shape, make the thumbnail wider (16:9-ish so hero images preview usefully)
  const isRect = shape === 'rect';
  const thumbW = isRect ? Math.round(previewSize * 1.6) : previewSize;
  const thumbH = previewSize;

  const validate = (file) => {
    if (!IMG_ALLOWED.includes(file.type)) {
      toast.error('Unsupported format. Please use JPG, PNG, WEBP, GIF, or SVG.');
      return false;
    }
    if (file.size > IMG_MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${IMG_MAX_MB} MB.`);
      return false;
    }
    return true;
  };

  const uploadFile = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', folder);
      const r = await fetch(`${API_URL}/api/media/upload`, { method: 'POST', body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await r.json();
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const startUploadFlow = (file) => {
    if (!validate(file)) return;
    if (cropEnabled && SHOULD_CROP(file.type)) {
      setPendingFile(file);
    } else {
      uploadFile(file);
    }
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) startUploadFlow(f);
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) startUploadFlow(f);
  };
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const preview = resolveImgSrc(value);
  const criteria = hint || (
    cropEnabled
      ? `${DEFAULT_HINT} · will be cropped to 1:1`
      : `${DEFAULT_HINT}`
  );

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex gap-2 items-start">
        <div
          onDragEnter={(e) => { stop(e); setDragOver(true); }}
          onDragLeave={(e) => { stop(e); setDragOver(false); }}
          onDragOver={stop}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
          className={`relative border-2 flex-shrink-0 cursor-pointer overflow-hidden transition-colors flex items-center justify-center ${previewClass}
            ${dragOver ? 'border-[#00457f] bg-blue-50' : preview ? 'border-gray-200 hover:border-[#00457f]' : 'border-dashed border-gray-300 hover:border-[#00457f] bg-gray-50'}`}
          style={{ width: thumbW, height: thumbH }}
          title={preview ? 'Click to replace image' : 'Click or drop an image here to upload'}
          data-testid={`${testId}-dropzone`}
        >
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <ImagePlus className="w-5 h-5 text-gray-400" />
          )}
          {busy && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-[#00457f] animate-spin" />
            </div>
          )}
        </div>

        <div className="flex-1 space-y-1.5 min-w-0">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#00457f]"
            placeholder="Paste an image URL or upload a file →"
            data-testid={`${testId}-url`}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              data-testid={`${testId}-upload-btn`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {busy ? 'Uploading…' : 'Upload'}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                data-testid={`${testId}-remove-btn`}
              >
                <X className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
          <p className="text-[11px] text-gray-500 leading-snug">{criteria}</p>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onFilePicked}
      />

      {pendingFile && (
        <ImageCropModal
          file={pendingFile}
          shape={shape}
          onCancel={() => setPendingFile(null)}
          onConfirm={(croppedFile) => { setPendingFile(null); uploadFile(croppedFile); }}
        />
      )}
    </div>
  );
};

export default AvatarUpload;
